import {pluralize} from 'common/util';
import {Component} from 'panel';

import {registerMPElement} from '../../util/register-element';

import {
  ActionOption,
  FilterBarState,
  FilterBarTableColumn,
  FilterBarTableRow,
  FilterBarTableState,
  FilterResultsLabelFormatter,
  FilterSelectConfig,
  SearchChangeEvent,
  SelectChangeEvent,
  TabChangeEvent,
  TableAttrs,
  ToggleChangeEvent,
} from './types.d';

import {RowState} from './constants';

import {serializeAttributes} from 'common/util';

import template from './index.jade';

import css from './index.styl?inline';

class FilterBarTable extends Component<FilterBarTableState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      testref: `string`,
      'hide-filter-bar': {
        type: `boolean`,
        default: false,
      },
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        tabs: [],
        selectedTab: null,

        actionOptions: [],
        filterSelectConfigs: [],
        tableHeaderSelectConfig: null,
        filterSearchPlaceholder: null,
        filterResultsLabelFormatter: (rowCount) => `${rowCount} ${pluralize(`result`, rowCount)}`,
        filterRowCount: null,
        filterState: {},

        rows: [],
        columns: [],
        tableAttrs: {},
        rowExpandState: {},
      },
      helpers: {
        serializeAttributes,
        // Template data formatters

        includes: (propertyValues: string[] | Set<string> | string, value: string) => {
          if (propertyValues instanceof Set) {
            return propertyValues.has(value);
          } else {
            return propertyValues.includes(value);
          }
        },

        getTableRows: () =>
          this.state.rows
            // TODO @evnp - roll filterSelect row filter logic into mp-table
            .filter((row) =>
              this.state.filterSelectConfigs.every((config) => {
                const chosenFilterValue = this.getFilterState().selectValues[config.name];
                const customFilter = config.customFilters && config.customFilters[chosenFilterValue];
                return (
                  !chosenFilterValue ||
                  chosenFilterValue === config.noFilterLabel ||
                  (row[config.name] && this.helpers.includes(row[config.name], chosenFilterValue)) ||
                  (customFilter && customFilter(row, row[config.name]))
                );
              }),
            )
            // TODO @evnp - roll expand/collapse logic into mp-table
            .map((row) => {
              const isExpandable = row.childRows && row.childRows.length;
              const isExpanded = isExpandable && this.isRowExpanded(row.key);
              return Object.assign(row, {
                isExpandable,
                isExpanded,
                toggleExpand: () => isExpandable && this.expandCollapseRow(row.key, !isExpanded),
              });
            }),

        getResultsLabel: () => {
          const {filterRowCount, rows, filterResultsLabelFormatter} = this.state;

          return filterRowCount !== null
            ? filterResultsLabelFormatter(filterRowCount)
            : filterResultsLabelFormatter(rows.length);
        },

        getTabsTestRef: () => {
          const testref = this.attr(`testref`);
          if (testref) {
            return `${testref}-tabs`;
          }
          return undefined;
        },

        getFilterBarTestRef: () => {
          const testref = this.attr(`testref`);
          if (testref) {
            return `${testref}-filter-bar`;
          }
          return undefined;
        },

        getFilterSearchText: () => this.getFilterState().searchText,

        getSelectedCount: () => this.state.rows.filter((row) => row.isSelected).length,

        getActionOptions: () => this.state.actionOptions,

        getFilterSelectOptions: () =>
          this.state.filterSelectConfigs.map((config) => ({
            ...config,
            options: config.options.map((label) => ({
              label,
              selected: label === (this.getFilterState().selectValues[config.name] || config.noFilterLabel),
            })),
          })),

        getTableAttrs: (): TableAttrs => {
          const {tableAttrs, columns} = this.state;
          const testref = this.attr(`testref`);
          const filterSearchText = this.helpers.getFilterSearchText();
          return {
            'default-sort-field': columns[0].field,
            ...(filterSearchText ? {'search-filter': filterSearchText} : {}),
            ...(testref ? {testref: `${testref}-table`} : {}),
            ...tableAttrs,
          };
        },

        getTableHeaderSelectConfig: () => this.state.tableHeaderSelectConfig,
        // Event handlers

        onTabChange: (ev: ToggleChangeEvent) => {
          const tabChangeEvent: TabChangeEvent = new CustomEvent(`tabChange`, {
            detail: {
              selectedTab: ev.detail.selected,
            },
          });
          this.dispatchEvent(tabChangeEvent);
        },

        onSelectionChange: (ev) => {
          const changedRows = Object.assign(
            {},
            ...ev.detail.changed.map((item) => ({
              [item.row.key]: Object.assign(item.row, {isSelected: item.isSelected}),
            })),
          );
          this.update({rows: this.state.rows.map((row) => changedRows[row.key] || row)});
          this.dispatchEvent(
            new CustomEvent(`selectionChange`, {
              detail: this.state.rows.filter((row) => row.isSelected),
            }),
          );
        },

        onRowClick: ({detail}) => this.dispatchEvent(new CustomEvent(`rowClick`, {detail})),

        onCellClick: ({detail}) => this.dispatchEvent(new CustomEvent(`cellClick`, {detail})),

        onClickAction: (ev) => this.dispatchEvent(new CustomEvent(`clickAction`, {detail: ev.detail})),

        onClearSelected: () => {
          this.update({rows: this.state.rows.map((row) => Object.assign(row, {isSelected: false}))});
          this.dispatchEvent(
            new CustomEvent(`selectionChange`, {
              detail: this.state.rows.filter((row) => row.isSelected),
            }),
          );
        },

        onTableResultsChange: (ev) => {
          this.update({filterRowCount: ev.detail.numResults});
          return this.dispatchEvent(new CustomEvent(`numResultsChange`, {detail: ev.detail}));
        },

        onFilterSearchChange: (ev: SearchChangeEvent) => this.updateFilterState({searchText: ev.detail.value}),

        onFilterSelectChange: (ev: SelectChangeEvent) => {
          const {name, value} = ev.detail;
          this.updateFilterSelectState(name, value);
          this.dispatchEvent(
            new CustomEvent(`changeFilter`, {
              detail: ev.detail,
            }),
          );
          // RAF needed so we don't scroll before new list is rendered and end up at bottom of page
          requestAnimationFrame(() => window.scrollTo(0, 0));
        },
        handleTableHeaderSelectChange: (ev) => {
          this.dispatchEvent(
            new CustomEvent(`tableHeaderSelectChange`, {
              detail: {
                ...ev.detail,
              },
            }),
          );
        },
      },
    };
  }

  set tabs(tabs: string[]) {
    this.update({tabs});
  }

  set selectedTab(selectedTab: string) {
    this.update({selectedTab});
  }

  set filterSelectConfigs(filterSelectConfigs: FilterSelectConfig[]) {
    this.update({filterSelectConfigs});
  }

  set tableHeaderSelectConfig(tableHeaderSelectConfig) {
    this.update({tableHeaderSelectConfig});
  }

  set filterSearchPlaceholder(filterSearchPlaceholder: string) {
    this.update({filterSearchPlaceholder});
  }

  set actionOptions(actionOptions: ActionOption[]) {
    this.update({actionOptions});
  }

  set filterResultsLabelFormatter(filterResultsLabelFormatter: FilterResultsLabelFormatter) {
    this.update({filterResultsLabelFormatter});
  }

  set rows(rows: FilterBarTableRow[]) {
    this.update({rows});
  }

  set columns(columns: FilterBarTableColumn[]) {
    this.update({columns});
  }

  set tableAttrs(tableAttrs: TableAttrs) {
    this.update({tableAttrs});
  }

  private getFilterState(): FilterBarState {
    const tab = this.state.selectedTab;
    return (
      (this.state.filterState && this.state.filterState[tab]) || {
        searchText: ``,
        selectValues: {},
      }
    );
  }

  private updateFilterState(newState: FilterBarState) {
    const tab = this.state.selectedTab;
    this.update({
      filterState: {
        ...(this.state.filterState || {}),
        [tab]: {
          ...this.getFilterState(),
          ...newState,
        },
      },
    });
  }

  private updateFilterSelectState(name: string, value: string) {
    this.updateFilterState({
      selectValues: {
        ...(this.getFilterState().selectValues || {}),
        [name]: value,
      },
    });
  }

  private expandCollapseRow(rowKey: string, expand: boolean) {
    const rowExpandState = this.state.rowExpandState;
    this.update({
      rowExpandState: {
        ...rowExpandState,
        [rowKey]: expand ? RowState.Expanded : RowState.Collapsed,
      },
    });
  }

  private isRowExpanded(rowKey: string): boolean {
    const searchText = this.helpers.getFilterSearchText();
    const rowExpandState = this.state.rowExpandState[rowKey];
    const expanded = rowExpandState === RowState.Expanded;
    const collapsed = rowExpandState === RowState.Collapsed;
    // expand all rows if searchText length > 2 to reveal matched properties
    // respect manual expand/collapse of rows
    // set minimum searchText length to avoid browser hang when typing -
    // if search text is single char almost ALL event rows will be expanded at once
    return (searchText && searchText.length > 2 && !collapsed) || expanded;
  }
}

export default registerMPElement(`mp-filter-bar-table`, FilterBarTable);
